import { useEffect, useRef } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useChatChannelConfig } from '@liveHelper/context/ChatChannelConfigContext'
import {
  conversationSelector,
  isTransferringSelector,
  queuePositionSelector,
  useStore,
  useStoreApi,
} from '@liveHelper/models/store'

import { uuidv4 } from '@core/helpers/uuidv4'
import { usePrevious } from '@core/hooks/usePrevious'
import { useContextTranslation } from '@core/i18n'

const createFakeAgentMessage = (
  content: string,
): one_to_one.shared.Message => ({
  client_msg_ref: uuidv4(),
  created_at: new Date().toISOString(),
  text_message: {
    content,
    author: 'AUTHOR_AGENT',
  },
})

/**
 * Internal logic of queue position dispatching messages to chat
 */
export const useQueuePositionUpdate = () => {
  const { t } = useContextTranslation()
  const queuePosition = useStore(queuePositionSelector)
  const previousQueuePosition = usePrevious(queuePosition)
  const conversation = useStore(conversationSelector)
  const previousConversation = usePrevious(conversation)
  const connectedMessageShown = useRef(false)
  const chatChannelConfig = useChatChannelConfig()
  const isTransferring = useStore(isTransferringSelector)

  const storeApi = useStoreApi()
  useEffect(() => {
    const { actions } = storeApi.getState()
    if (previousConversation && !conversation) {
      actions.setQueuePosition(undefined)
      connectedMessageShown.current = false
      return
    }
    if (isTransferring) {
      // when transferring, we want to shown connecting message again
      // and also want to stop queue position updates
      connectedMessageShown.current = false
      return
    }
    if (connectedMessageShown.current) {
      return
    }
    if (conversation?.agent) {
      const message: one_to_one.shared.Message = {
        client_msg_ref: uuidv4(),
        created_at: new Date().toISOString(),
        system_message: {
          content: t('oneToOne:You are connected to {{agentName}}', {
            agentName: conversation.agent.name,
          }),
          type: 'SYSTEM_MESSAGE_TYPE_INFO',
        },
      }
      actions.addChatMessages([message], 'visitor')
      actions.setQueuePosition(undefined)
      connectedMessageShown.current = true
    } else if (!previousQueuePosition && queuePosition) {
      // If the waitlist message will be sent by BE, do not send it from FE.
      if (!chatChannelConfig?.feature_flags?.include_system_messages) {
        const messages: one_to_one.shared.Message[] = []
        if (chatChannelConfig?.widget_style?.waitlist_message) {
          messages.push(
            createFakeAgentMessage(
              chatChannelConfig.widget_style.waitlist_message,
            ),
          )
        }
        messages.push(
          createFakeAgentMessage(
            t('oneToOne:Your position in the queue: {{queuePosition}}.', {
              queuePosition,
            }),
          ),
        )
        actions.addChatMessages(messages, 'visitor')
      }
    } else if (
      previousQueuePosition &&
      previousQueuePosition !== queuePosition &&
      queuePosition
    ) {
      if (!chatChannelConfig?.feature_flags?.include_system_messages) {
        const message = createFakeAgentMessage(
          t('oneToOne:Your position in the queue: {{queuePosition}}.', {
            queuePosition,
          }),
        )
        actions.addChatMessages([message], 'visitor')
      }
    }
  }, [
    storeApi,
    conversation,
    previousConversation,
    queuePosition,
    previousQueuePosition,
    chatChannelConfig?.feature_flags?.include_system_messages,
    chatChannelConfig?.widget_style?.waitlist_message,
    isTransferring,
    t,
  ])
}
